import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  MoveOrgUnitTierRequest,
  MoveTierLevelRequest,
  OrgUnitListItem,
  OrgUnitOption,
  OrgUnitRequest,
  OrgUnitTypeListItem,
  OrgUnitTypeOption,
  OrgUnitTypeRequest,
  OrgUnitTreeNode,
} from './organizationTypes';

/** The chart's list query adds the tier facet to the shared contract. */
interface OrgUnitQuery extends ListQuery {
  /** Comma-separated unit-type ids. */
  type?: string;
}

function toQueryString(query: OrgUnitQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  /*
    Facets are sent whenever the key is present, empty string included — an empty value is the
    reader saying "none of these", which matches no row. A truthiness check here would drop it
    and the server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);
  if (query.type !== undefined) params.set('type', query.type);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const orgUnitTypesApi = {
  /** Soft-deletes every record in the module at once; resolves with how many were deleted. */
  clear: () => api<number>('/api/org-unit-types/clear', { method: 'POST' }),

  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<OrgUnitTypeListItem>>(`/api/org-unit-types${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<OrgUnitTypeListItem>(`/api/org-unit-types/${id}`, { signal }),

  /**
   * Active types for the unit form's picker. Unpaged on purpose — a picker that stopped at
   * a page boundary would drop choices without saying so.
   */
  options: (signal?: AbortSignal) =>
    api<OrgUnitTypeOption[]>('/api/org-unit-types/options', { signal }),

  create: (request: OrgUnitTypeRequest) =>
    api<OrgUnitTypeListItem>('/api/org-unit-types', { method: 'POST', body: request }),

  update: (id: string, request: OrgUnitTypeRequest) =>
    api<OrgUnitTypeListItem>(`/api/org-unit-types/${id}`, { method: 'PUT', body: request }),

  /**
   * Moves a tier to another level; resolves with how many tiers were written.
   *
   * Its own endpoint rather than a field on the update, because a level is a position among
   * the other tiers and the write touches rows the request never names.
   */
  moveLevel: (id: string, request: MoveTierLevelRequest) =>
    api<number>(`/api/org-unit-types/${id}/move-level`, { method: 'POST', body: request }),

  /** Soft delete. Units already filed under the type keep resolving its name. */
  deactivate: (id: string) => api<void>(`/api/org-unit-types/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/org-unit-types/${id}/restore`, { method: 'POST' }),
};

export const orgUnitsApi = {
  /** Soft-deletes every record in the module at once; resolves with how many were deleted. */
  clear: () => api<number>('/api/org-units/clear', { method: 'POST' }),

  list: (query: OrgUnitQuery, signal?: AbortSignal) =>
    api<PagedResult<OrgUnitListItem>>(`/api/org-units${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<OrgUnitListItem>(`/api/org-units/${id}`, { signal }),

  /**
   * The whole chart, nested, for the Preview surface.
   *
   * Deleted units are hidden unless asked for — the chart shows the organization as it
   * stands.
   */
  tree: (includeDeleted = false, signal?: AbortSignal) =>
    api<OrgUnitTreeNode[]>(
      `/api/org-units/tree${includeDeleted ? '?includeDeleted=true' : ''}`,
      { signal },
    ),

  /**
   * Active units for the parent picker.
   *
   * `excludeSubtreeOf` drops the unit being edited and everything under it, so the control
   * cannot offer a parent the server will reject as a cycle.
   */
  options: (excludeSubtreeOf?: string, signal?: AbortSignal) =>
    api<OrgUnitOption[]>(
      `/api/org-units/options${excludeSubtreeOf ? `?excludeSubtreeOf=${excludeSubtreeOf}` : ''}`,
      { signal },
    ),

  create: (request: OrgUnitRequest) =>
    api<OrgUnitListItem>('/api/org-units', { method: 'POST', body: request }),

  update: (id: string, request: OrgUnitRequest) =>
    api<OrgUnitListItem>(`/api/org-units/${id}`, { method: 'PUT', body: request }),

  /**
   * Moves a unit to another tier; resolves with how many records were written.
   *
   * Its own endpoint rather than a field on the update, because the number of rows it
   * touches depends on the tree rather than on the request.
   */
  moveTier: (id: string, request: MoveOrgUnitTierRequest) =>
    api<number>(`/api/org-units/${id}/move-tier`, { method: 'POST', body: request }),

  /** Soft delete. Refused while the unit still has active children. */
  deactivate: (id: string) => api<void>(`/api/org-units/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/org-units/${id}/restore`, { method: 'POST' }),
};
